import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  Table,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

interface UseDataTableOptions<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];

  // Pagination Options
  pageCount?: number;
  pageSize?: number;
  initialPageIndex?: number;

  // Selection Options
  enableRowSelection?: boolean;
  enableMultiRowSelection?: boolean;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;

  // Sort Options
  enableSorting?: boolean;
  enableMultiSort?: boolean;
  initialSorting?: SortingState;

  // Filters Options
  enableFiltering?: boolean;
  initialFilters?: ColumnFiltersState;

  // Visibility Options
  initialColumnVisibility?: VisibilityState;

  // Pinning Options
  initialColumnPinning?: ColumnPinningState;

  // Expansion Options
  enableExpanding?: boolean;
  getSubRows?: (originalRow: TData, index: number) => TData[] | undefined;
  initialExpanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;

  // Other Options
  manualPagination?: boolean;
  manualFiltering?: boolean;
  manualSorting?: boolean;
  debugTable?: boolean;

  // Custom metadata
  meta?: any;
}

/**
 * Custom hook to create Tanstack Table with expansion support
 * Returns the Table instance directly for easier usage with DataTable component
 */
export function useDataTable<TData>({
                                      data,
                                      columns,
                                      pageCount,
                                      pageSize = 10,
                                      initialPageIndex = 0,
                                      enableRowSelection = false,
                                      enableMultiRowSelection = true,
                                      onRowSelectionChange,
                                      enableSorting = true,
                                      enableMultiSort = false,
                                      initialSorting = [],
                                      enableFiltering = true,
                                      initialFilters = [],
                                      initialColumnVisibility = {},
                                      initialColumnPinning = {},
                                      enableExpanding = false,
                                      getSubRows,
                                      initialExpanded = {},
                                      onExpandedChange,
                                      manualPagination = false,
                                      manualFiltering = false,
                                      manualSorting = false,
                                      debugTable = false,
                                      meta,
                                    }: UseDataTableOptions<TData>): Table<TData> {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(initialColumnVisibility);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(initialFilters);
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(initialColumnPinning);
  const [expanded, setExpanded] = React.useState<ExpandedState>(initialExpanded);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: pageSize,
  });

  // Reset pagination when data changes
  React.useEffect(() => {
    if (!manualPagination) {
      setPagination(prev => ({
        ...prev,
        pageIndex: 0,
      }));
    }
  }, [data, manualPagination]);

  // Row selection management
  const handleRowSelectionChange = React.useCallback(
    (updater: OnChangeFn<RowSelectionState>) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(updater);
      }
      setRowSelection(updater);
    },
    [onRowSelectionChange]
  );

  // Expansion management
  const handleExpandedChange = React.useCallback(
    (updater: OnChangeFn<ExpandedState>) => {
      if (onExpandedChange) {
        onExpandedChange(updater);
      }
      setExpanded(updater);
    },
    [onExpandedChange]
  );

  // Default getSubRows function if not provided
  const defaultGetSubRows = React.useCallback(
    (originalRow: TData) => {
      // Assume subRows property exists on the data object
      return (originalRow as any)?.subRows;
    },
    []
  );

  // Create and return the table instance directly
  return useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination,
      columnPinning,
      ...(enableExpanding && {expanded}),
    },
    pageCount,
    enableRowSelection,
    enableMultiRowSelection,
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onPaginationChange: setPagination,
    ...(enableExpanding && {
      onExpandedChange: handleExpandedChange,
      getSubRows: getSubRows || defaultGetSubRows,
    }),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    ...(enableExpanding && {getExpandedRowModel: getExpandedRowModel()}),
    manualPagination,
    manualFiltering,
    manualSorting,
    debugTable,
    enableSorting,
    enableMultiSort,
    enableFiltering,
    meta,
  });
}